import React, { useState, useEffect } from 'react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { getApiBaseUrl, getHeaders } from '../services/api';
import { useTranslation } from '../context/LanguageContext';

const LSTMDefectAnalysis = ({ selectedShotNumber, shotHistoryData = [], equipmentId, craftId }) => {
  const { t } = useTranslation();
  const [lstmData, setLstmData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lstmShotHistory, setLstmShotHistory] = useState([]); // LSTM용 샷 히스토리 (선택 샷 기준 이전 50개)

  // LSTM 분석 데이터 및 샷 히스토리 로드
  useEffect(() => {
    const fetchLSTMData = async () => {
      if (!equipmentId || !craftId) {
        setError(t('components.lstmDefect.missingIds'));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // 1. LSTM 분석 데이터 조회
        const lstmResponse = await fetch(
          `${getApiBaseUrl()}/lstm/analyze/${equipmentId}/${craftId}${selectedShotNumber ? `?shot_number=${selectedShotNumber}` : ''}`
        );

        if (!lstmResponse.ok) {
          throw new Error(`LSTM API 오류: ${lstmResponse.status}`);
        }

        const lstmResult = await lstmResponse.json();
        // API 응답 구조: { success: true, data: { ... } }
        setLstmData(lstmResult?.data || lstmResult);

        // 2. 선택된 샷 기준 이전 50개 샷 히스토리 조회
        // 파라미터 변화 추이 차트는 craft_id 필터 없이 조회 (설비 전체 샷 기준)
        let shotHistoryUrl = `${getApiBaseUrl()}/machine/shot-history/${equipmentId}?limit=50`;
        if (selectedShotNumber) {
          shotHistoryUrl += `&before_shot=${selectedShotNumber}`;
        }
        // craft_id 필터는 제거 - 설비의 모든 샷에서 선택 샷 기준 이전 50개 조회

        const historyResponse = await fetch(shotHistoryUrl, {
          method: 'GET',
          headers: getHeaders()
        });

        if (historyResponse.ok) {
          const historyData = await historyResponse.json();
          // API 응답 구조: { success: true, data: { shots: [...] } }
          const shots = historyData?.data?.shots || historyData?.shots || historyData || [];
          setLstmShotHistory(shots);
        }

        setError(null);
      } catch (err) {
        console.error('LSTM 데이터 로드 실패:', err);
        setError(err.message);
        setLstmData(null);
        setLstmShotHistory([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLSTMData();
  }, [equipmentId, craftId, selectedShotNumber]);

  // Attention Weight 차트 데이터 생성
  const attentionChartData = lstmData?.attention_weights?.map((aw, idx) => ({
    shot: (aw.label || `Shot ${aw.shot_index || idx + 1}`).replace('Shot ', ''),  // "Shot " 제거, 숫자만 표시
    attention: aw.weight || 0,
    label: aw.label || ''
  })) || [];

  // 주요 파라미터 변화 추이 데이터 생성 (선택된 샷 기준으로 이전 50샷, 실제값 표시)
  const timeSeriesData = React.useMemo(() => {
    let dataSource = [];

    if (lstmShotHistory.length > 0) {
      dataSource = lstmShotHistory;
    } else if (shotHistoryData.length > 0) {
      if (selectedShotNumber) {
        const selectedIdx = shotHistoryData.findIndex(s => s.shotCount === selectedShotNumber);
        if (selectedIdx !== -1) {
          dataSource = shotHistoryData.slice(selectedIdx, selectedIdx + 50);
        } else {
          dataSource = shotHistoryData.slice(0, 50);
        }
      } else {
        dataSource = shotHistoryData.slice(0, 50);
      }
    }

    if (!dataSource || dataSource.length === 0) return [];

    // reverse하여 오래된 샷이 왼쪽, 선택된 샷이 오른쪽에 오도록
    const recentShots = [...dataSource].reverse();

    return recentShots.map((shot, idx) => ({
      shot: shot.shotCount || idx + 1,
      cushion: (shot.injEndPosi || shot.cushion) ? Number(shot.injEndPosi || shot.cushion) : null,
      temperature: shot.temp1Current ? Number(shot.temp1Current) : null,
      maxPress: (shot.injMaxPress || shot.injMaxPress === 0) ? Number(shot.injMaxPress) : null,
    }));
  }, [lstmShotHistory, shotHistoryData, selectedShotNumber]);

  // 주요 원인 인자 중요도 (실제 데이터 사용)
  const factorImportance = React.useMemo(() => {
    if (!lstmData?.feature_importance || lstmData.feature_importance.length === 0) {
      return [];
    }

    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#FFA07A', '#98D8C8'];

    return lstmData.feature_importance.slice(0, 5).map((item, idx) => ({
      factor: item.feature_kr || item.feature,
      importance: item.importance / 100, // 0-1 범위로 정규화
      color: colors[idx % colors.length],
      rank: item.rank
    }));
  }, [lstmData]);

  // 레이더 차트 데이터 (실제 추천값 기반, 0-10 정규화)
  const radarData = React.useMemo(() => {
    if (!lstmData?.recommendations || lstmData.recommendations.length === 0) {
      return [];
    }

    // 상위 5개 추천값 사용
    const topRecs = lstmData.recommendations.slice(0, 5);

    return topRecs.map((rec) => {
      const currentValue = rec.current_value ?? 0;
      const optimalValue = rec.recommended_value ?? 0;
      const normalMax = optimalValue * 1.05;  // 상한 (추천값 +5%)
      const normalMin = optimalValue * 0.95;  // 하한 (추천값 -5%)

      // 정상범위를 기준으로 0-10 정규화
      // normalMin=0, optimalValue=5, normalMax=10
      const normalize = (value) => {
        if (normalMax === normalMin) return 5;
        const ratio = (value - normalMin) / (normalMax - normalMin);
        return ratio * 10; // 0~10 범위로 변환 (범위 밖은 음수 또는 10 초과)
      };

      return {
        category: rec.feature_kr || rec.feature,
        current: normalize(currentValue),
        optimal: 5,  // 추천값은 항상 중간값 5
        normal: 10   // 정상범위 상한은 항상 10
      };
    });
  }, [lstmData]);

  // 추천 조정값 (lstm_recommendations 데이터 사용)
  const recommendations = React.useMemo(() => {
    if (!lstmData?.recommendations || lstmData.recommendations.length === 0) {
      return [];
    }

    return lstmData.recommendations.slice(0, 5).map((rec) => {
      const currentVal = rec.current_value != null ? rec.current_value.toFixed(2) : '0.00';
      const recommendedVal = rec.recommended_value != null ? rec.recommended_value.toFixed(2) : '0.00';
      const currentNum = rec.current_value ?? 0;
      const recommendedNum = rec.recommended_value ?? 0;
      const change = (recommendedNum - currentNum).toFixed(2);
      const changeStr = change >= 0 ? `+${change}` : change;

      // 추천값 대비 실제값 비율 계산 (0-200%)
      let gaugeValue = 100; // 기본값 100%
      if (recommendedNum !== 0) {
        const ratio = (currentNum / recommendedNum) * 100;
        gaugeValue = Math.min(Math.max(ratio, 0), 200); // 0-200 범위로 제한
      }

      // 80% 이하 또는 120% 이상이면 out_of_range
      const isOutOfRange = gaugeValue <= 80 || gaugeValue >= 120;

      return {
        parameter: rec.feature_kr || rec.feature,
        current: currentVal,
        recommended: recommendedVal,
        change: changeStr,
        gaugeValue: Math.round(gaugeValue),
        status: isOutOfRange ? 'out_of_range' : 'normal',
        direction: rec.direction,
        originalStatus: rec.status
      };
    });
  }, [lstmData]);

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gridTemplateRows: '3fr 3fr 1fr',
      gap: '3px',
      height: '100%',
      backgroundColor: '#0D1117'
    }}>
      {/* 1행 1~2열: 시간 구간별 Attention Weight 분석 */}
      <div style={{ gridColumn: '1 / 3', backgroundColor: '#21252b', padding: '16px', border: '1px solid #1B1D20', display: 'flex', flexDirection: 'column' }}>
        <h4 style={{ fontSize: '16px', fontWeight: '600', color: 'white', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #3F4550', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span className="material-symbols-outlined" style={{ fontSize: '20px', color: '#60A5FA' }}>show_chart</span>
          {t('components.lstmDefect.sectionAttention')}
        </h4>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, color: '#9CA3AF' }}>
            {t('components.lstmDefect.loading')}
          </div>
        ) : error || attentionChartData.length === 0 ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, color: '#9CA3AF' }}>
            {t('components.lstmDefect.noData')}
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={attentionChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis
                  dataKey="shot"
                  stroke="#9CA3AF"
                  style={{ fontSize: '11px' }}
                  interval="preserveStartEnd"
                />
                <YAxis stroke="#9CA3AF" domain={['auto', 'auto']} style={{ fontSize: '11px' }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', fontSize: '12px' }}
                  labelStyle={{ fontSize: '13px' }}
                  formatter={(value) => [value.toFixed(3), 'Attention']}
                />
                <defs>
                  <linearGradient id="attentionGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#EF4444" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#EF4444" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <Area type="monotone" dataKey="attention" stroke="#EF4444" fill="url(#attentionGradient)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
            <div style={{ marginTop: '8px', fontSize: '11px', color: '#9CA3AF' }}>
              {t('components.lstmDefect.anomalySummary', { score: lstmData?.anomaly_score?.toFixed(3), threshold: lstmData?.threshold?.toFixed(3) })}
              {lstmData?.is_anomaly ? <span style={{ color: '#EF4444', fontWeight: '600' }}>{t('components.lstmDefect.anomalyDetected')}</span> : <span style={{ color: '#10B981', fontWeight: '600' }}>{t('components.lstmDefect.anomalyNormal')}</span>}
            </div>
          </>
        )}
      </div>

      {/* 1행 3열: 불량 원인 인자 중요도 */}
      <div style={{ backgroundColor: '#21252b', padding: '16px', border: '1px solid #1B1D20', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <h4 style={{ fontSize: '16px', fontWeight: '600', color: 'white', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #3F4550' }}>
          {t('components.lstmDefect.sectionFactor')}
        </h4>
        {factorImportance.length === 0 ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, color: '#9CA3AF' }}>
            데이터가 없습니다
          </div>
        ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', flex: 1, overflowY: 'auto' }}>
          {factorImportance.map((item, idx) => (
            <div
              key={idx}
              style={{
                padding: '8px',
                backgroundColor: '#2D3339',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#30363D'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2D3339'}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '5px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: item.color }} />
                  <span style={{ fontWeight: '600', color: '#F3F4F6', fontSize: '12px' }}>{item.factor}</span>
                </div>
                <span style={{ fontSize: '11px', color: item.trend > 0 ? '#EF4444' : '#10B981', fontWeight: '600' }}>
                  {item.trend > 0 ? '+' : ''}{item.trend}%
                </span>
              </div>
              <div style={{ width: '100%', height: '6px', backgroundColor: '#1B1D20', borderRadius: '3px', overflow: 'hidden' }}>
                <div
                  style={{
                    height: '100%',
                    width: `${item.importance * 100}%`,
                    backgroundColor: item.color,
                    transition: 'width 1s ease'
                  }}
                />
              </div>
              <div style={{ marginTop: '5px', fontSize: '11px', color: '#9CA3AF' }}>
                {t('components.lstmDefect.importance', { value: (item.importance * 100).toFixed(0) })}
              </div>
            </div>
          ))}
        </div>
        )}
      </div>

      {/* 2행 1~2열: 주요 파라미터 변화 추이 (3개 개별 차트) */}
      <div style={{ gridColumn: '1 / 3', backgroundColor: '#21252b', padding: '12px', border: '1px solid #1B1D20', display: 'flex', flexDirection: 'column' }}>
        <h4 style={{ fontSize: '16px', fontWeight: '600', color: 'white', marginBottom: '8px', paddingBottom: '6px', borderBottom: '1px solid #3F4550', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span className="material-symbols-outlined" style={{ fontSize: '20px', color: '#60A5FA' }}>insights</span>
          {t('components.lstmDefect.sectionParamTrend')}
        </h4>
        {timeSeriesData.length === 0 ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, color: '#9CA3AF' }}>
            {t('components.lstmDefect.noData')}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', flex: 1, minHeight: 0 }}>
            {/* 쿠션량 */}
            <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: '#9CA3AF', marginBottom: '4px' }}>{t('components.lstmDefect.paramTrend.cushion')}</div>
              <div style={{ flex: 1, minHeight: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={timeSeriesData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="shot" stroke="#9CA3AF" style={{ fontSize: '9px' }} interval="preserveStartEnd" />
                    <YAxis stroke="#9CA3AF" style={{ fontSize: '9px' }} domain={['auto', 'auto']} tickFormatter={(v) => v.toFixed(1)} width={40} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', fontSize: '11px' }}
                      formatter={(value) => value !== null ? [`${value.toFixed(2)} mm`, t('components.lstmDefect.paramTrend.cushionLabel')] : 'N/A'}
                    />
                    <Line type="monotone" dataKey="cushion" stroke="#45B7D1" strokeWidth={2} dot={false} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            {/* 노즐온도 */}
            <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: '#9CA3AF', marginBottom: '4px' }}>{t('components.lstmDefect.paramTrend.nozzleTemp')}</div>
              <div style={{ flex: 1, minHeight: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={timeSeriesData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="shot" stroke="#9CA3AF" style={{ fontSize: '9px' }} interval="preserveStartEnd" />
                    <YAxis stroke="#9CA3AF" style={{ fontSize: '9px' }} domain={['auto', 'auto']} tickFormatter={(v) => v.toFixed(1)} width={40} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', fontSize: '11px' }}
                      formatter={(value) => value !== null ? [`${value.toFixed(1)} °C`, t('components.lstmDefect.paramTrend.nozzleTempLabel')] : 'N/A'}
                    />
                    <Line type="monotone" dataKey="temperature" stroke="#4ECDC4" strokeWidth={2} dot={false} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            {/* 최대사출압력 */}
            <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: '#9CA3AF', marginBottom: '4px' }}>{t('components.lstmDefect.paramTrend.maxPress')}</div>
              <div style={{ flex: 1, minHeight: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={timeSeriesData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="shot" stroke="#9CA3AF" style={{ fontSize: '9px' }} interval="preserveStartEnd" />
                    <YAxis stroke="#9CA3AF" style={{ fontSize: '9px' }} domain={['auto', 'auto']} tickFormatter={(v) => v.toFixed(0)} width={40} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', fontSize: '11px' }}
                      formatter={(value) => value !== null ? [`${value.toFixed(1)} bar`, t('components.lstmDefect.paramTrend.maxPressLabel')] : 'N/A'}
                    />
                    <Line type="monotone" dataKey="maxPress" stroke="#FF6B6B" strokeWidth={2} dot={false} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 2행 3열: 파라미터 상태 비교 */}
      <div style={{ backgroundColor: '#21252b', padding: '16px', border: '1px solid #1B1D20', display: 'flex', flexDirection: 'column' }}>
        <h4 style={{ fontSize: '16px', fontWeight: '600', color: 'white', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #3F4550' }}>
          {t('components.lstmDefect.sectionRadar')}
        </h4>
        {radarData.length === 0 ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, color: '#9CA3AF' }}>
            {t('components.lstmDefect.noData')}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={radarData}>
              <PolarGrid stroke="#30363D" />
              <PolarAngleAxis dataKey="category" stroke="#9CA3AF" style={{ fontSize: '11px' }} />
              <PolarRadiusAxis angle={90} domain={[0, 10]} tick={false} stroke="transparent" />
              <Radar
                name={t('components.lstmDefect.radarNormal')}
                dataKey="normal"
                stroke="#10B981"
                fill="#10B981"
                fillOpacity={0.2}
              />
              <Radar
                name={t('components.lstmDefect.radarCurrent')}
                dataKey="current"
                stroke="#EF4444"
                fill="#EF4444"
                fillOpacity={0.3}
              />
              <Radar
                name={t('components.lstmDefect.radarOptimal')}
                dataKey="optimal"
                stroke="#3B82F6"
                fill="#3B82F6"
                fillOpacity={0.1}
                strokeDasharray="5 5"
              />
              <Legend wrapperStyle={{ fontSize: '10px' }} iconSize={8} />
            </RadarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* 3행 1~2열: 공정 튜닝 추천값 */}
      <div style={{ gridColumn: '1 / 3', backgroundColor: '#21252b', padding: '16px', border: '1px solid #1B1D20', display: 'flex', flexDirection: 'column' }}>
        <h4 style={{ fontSize: '16px', fontWeight: '600', color: 'white', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #3F4550' }}>
          {t('components.lstmDefect.sectionRecommendations')}
        </h4>
        {recommendations.length === 0 ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, color: '#9CA3AF' }}>
            {t('components.lstmDefect.noData')}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px', flex: 1 }}>
            {recommendations.map((rec, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '8px 10px',
                  backgroundColor: rec.status === 'out_of_range' ? '#2D1F1F' : '#2D3339',
                  border: rec.status === 'out_of_range' ? '1px solid #EF4444' : '1px solid transparent',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = rec.status === 'out_of_range' ? '#3D2525' : '#30363D'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = rec.status === 'out_of_range' ? '#2D1F1F' : '#2D3339'}
              >
                <div style={{ fontWeight: '600', color: '#F3F4F6', marginBottom: '4px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {rec.parameter}
                  {rec.status === 'out_of_range' && <span style={{ fontSize: '10px', color: '#EF4444' }}>⚠</span>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <div style={{ fontSize: '10px', color: '#9CA3AF' }}>
                    {rec.current} → {rec.recommended}
                  </div>
                  <span style={{ color: rec.change.startsWith('+') ? '#EF4444' : rec.change.startsWith('-') ? '#10B981' : '#9CA3AF', fontWeight: '700', fontSize: '11px' }}>
                    {rec.change}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <div style={{ flex: 1, height: '5px', backgroundColor: '#1B1D20', borderRadius: '2px', overflow: 'hidden', position: 'relative' }}>
                    {/* 100% 기준선 */}
                    <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: '1px', backgroundColor: '#6B7280', zIndex: 1 }} />
                    <div
                      style={{
                        height: '100%',
                        width: `${Math.min(rec.gaugeValue / 2, 100)}%`,
                        background: rec.status === 'out_of_range' ? 'linear-gradient(to right, #EF4444, #F87171)' : 'linear-gradient(to right, #10B981, #34D399)',
                        transition: 'width 1s ease'
                      }}
                    />
                  </div>
                  <span style={{ fontSize: '10px', fontWeight: '600', color: rec.status === 'out_of_range' ? '#EF4444' : '#10B981' }}>{rec.gaugeValue}%</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 3행 3열: 모델 예측 신뢰도 */}
      <div style={{ backgroundColor: '#21252b', padding: '16px', border: '1px solid #1B1D20', display: 'flex', flexDirection: 'column' }}>
        <h4 style={{ fontSize: '16px', fontWeight: '600', color: 'white', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #3F4550' }}>
          {t('components.lstmDefect.sectionConfidence')}
        </h4>
        {!lstmData?.confidence ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, color: '#9CA3AF' }}>
            {t('components.lstmDefect.noData')}
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '12px', flex: 1 }}>
            {/* 종합 신뢰도 (좌측) */}
            <div style={{ padding: '12px', backgroundColor: '#2D3339', flex: '0 0 auto', display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: '120px' }}>
              <div style={{ fontSize: '11px', color: '#9CA3AF', marginBottom: '5px' }}>{t('components.lstmDefect.overallConfidence')}</div>
              <div style={{ fontSize: '24px', fontWeight: '800', color: '#10B981' }}>
                {lstmData.confidence.overall?.toFixed(1) ?? '0.0'}%
              </div>
            </div>
            {/* 세부 지표 (우측) */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: '1', justifyContent: 'center' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#9CA3AF', fontSize: '12px' }}>{t('components.lstmDefect.lstmAccuracy')}</span>
                <span style={{ fontWeight: '700', color: '#10B981', fontSize: '13px' }}>
                  {lstmData.confidence.lstm_accuracy?.toFixed(1) ?? '0.0'}%
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#9CA3AF', fontSize: '12px' }}>{t('components.lstmDefect.attentionConfidence')}</span>
                <span style={{ fontWeight: '700', color: '#3B82F6', fontSize: '13px' }}>
                  {lstmData.confidence.attention_confidence?.toFixed(1) ?? '0.0'}%
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#9CA3AF', fontSize: '12px' }}>{t('components.lstmDefect.dataQuality')}</span>
                <span style={{ fontWeight: '700', color: '#F59E0B', fontSize: '13px' }}>
                  {lstmData.confidence.data_quality?.toFixed(1) ?? '0.0'}%
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LSTMDefectAnalysis;
